import { useRef } from "react";
import type { LinkCardCanvasObject } from "@refinement-canvas/domain";
import { isObjectPlacementTool, usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { CLICK_THRESHOLD_PX, useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { safeHostname } from "../../../lib/url-hostname";
import { useOgImageObjectUrl } from "../../../lib/og-image-source";
import { renderSearchHighlightedText, useSearchHighlightContext } from "./linkified-text";

/** 画像領域の高さ。サムネイル（canvas-thumbnail.ts）のリンクカード描画と同じ比率にそろえる。 */
const IMAGE_AREA_HEIGHT = "55%";

interface Props {
  readonly object: LinkCardCanvasObject;
}

// OGP リンクカード。
// リッチ（画像 + タイトル）/ ミニマム（タイトルのみ）/ テキストのみ の 3 段階フォールバックで描画する。
// クリックで選択、ドラッグで移動できる。リンクは Ctrl/Cmd + クリックで新規タブを開く挙動に切り替える
// （ドラッグ移動 UX とリンク遷移 UX が衝突するため、明示的なキー操作を要求する）。
// 加えて、修飾キーを知らない利用者でも遷移できるよう、ダブルクリックでも新規タブを開く
// （カードは本文編集を持たないため、ダブルクリックが空いている）。
// 遷移の起点が click / dblclick の 2 つになるため、「1 つの操作（1 回のクリック / ダブルクリック）で
// 開く別タブは 1 枚」を排他規則として置く。ブラウザは 1 回のダブルクリックで
// click(detail=1) → click(detail=2) → dblclick の順にイベントを送るので、
// - click は detail<=1 のときだけ開く（2 回目のクリックでは開かない）
// - dblclick は、その操作の中で click が既に開いていないときだけ開く
// とし、「既に開いたか」は操作単位の ref で持つ（1 回目と 2 回目で修飾キーの押下状態が変わっても
// 1 枚に保つため、dblclick 時点の修飾キーでは判定しない）。
export function LinkCardObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 参照専用（埋め込み）ではドラッグ移動が無効なため、プレーンクリックでリンクを開く。
  const readOnly = useCanvasStore((s) => s.readOnly);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  // 検索語の強調はタイトルにだけ当てる（Connect Note の検索索引が取り込むのはタイトルだけで、
  // 説明文とサイト名は一致し得ないため）。カード全体がリンクなので、解除用の属性は付けない。
  const search = useSearchHighlightContext();

  useObjectDrag({
    objectId: object.id,
    elementRef: ref,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // 画像領域を確保するかどうかは imageUrl の有無だけで決める。取得の成否で高さが変わると、
  // 画面とサムネイル（imageUrl の有無で 55% を空ける）のレイアウトが食い違うため。
  const hasImageArea = Boolean(object.imageUrl);
  // 相手サイトの応答ヘッダー（Cross-Origin-Resource-Policy / CORS）に影響されないよう、
  // 画像は同一オリジンの API 経由で取得した object URL から描く。
  // 万一 object URL の描画に失敗した場合も、壊れた画像アイコンを出さずプレースホルダーへ倒す
  // （reportRenderError が発行済みの object URL をその場で解放する）。
  const {
    src: imageSrc,
    failed: imageFailed,
    reportRenderError,
  } = useOgImageObjectUrl(object.imageUrl);

  // 遷移の起点が click / dblclick の 2 つになるため、URL 検証と window.open は 1 か所へ集約する。
  // 多層防御（既存データに javascript: / data: 等の危険スキームが残っていても遷移しない）を
  // 両経路で必ず通すためでもある。正規の link-card は常に http/https のため通常操作には影響しない。
  const openLinkInNewTab = (): void => {
    if (!isHttpUrl(object.url)) return;
    window.open(object.url, "_blank", "noopener,noreferrer");
  };

  // この操作（1 回のクリック / ダブルクリック）で既に別タブを開いたか。
  // 1 回目と 2 回目で修飾キーの押下状態が変わっても「1 操作 1 オープン」を保つために持つ。
  const openedInGestureRef = useRef(false);

  const onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    // ダブルクリックの 2 回目以降（detail>=2）では開かない。1 つの操作で開く別タブを 1 枚に保つ
    // （参照専用でダブルクリックすると 2 枚開いていた不具合の是正でもある）。
    if (event.detail > 1) return;
    // detail<=1 は新しい操作の 1 回目（detail=0 はプログラムから合成した click）。
    // ここで開封状態をリセットする。
    openedInGestureRef.current = false;
    // 参照専用ではプレーンクリックで遷移。編集モードではドラッグ移動と衝突しないよう
    // 従来どおり Ctrl/Cmd + クリックのときだけ遷移する。
    if (readOnly || event.ctrlKey || event.metaKey) {
      openLinkInNewTab();
      openedInGestureRef.current = true;
    }
  };

  // 直近の pointerdown の位置。ダブルクリックの 2 回目がドラッグだった場合を弾くために持つ。
  const pointerDownAtRef = useRef<{ x: number; y: number } | null>(null);

  // capture 相で記録するのは、useObjectDrag が同じ要素へ張る pointerdown リスナーより先に座標を
  // 取り、かつそのリスナーが stopPropagation してもここが呼ばれなくならないようにするため
  // （記録だけを行い、イベントの伝播は止めない）。
  const onPointerDownCapture = (event: React.PointerEvent<HTMLDivElement>): void => {
    pointerDownAtRef.current = { x: event.clientX, y: event.clientY };
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    // 1 回目のクリックで既に開いている場合（参照専用・Ctrl/Cmd 押下）は二重に開かない。
    if (openedInGestureRef.current) return;
    // 2 回目の押下がドラッグになっていたら遷移しない（移動の意図を優先する）。
    // しきい値と比較は useObjectDrag と同じ（distance <= CLICK_THRESHOLD_PX が「動いていない」）に
    // そろえ、「ドラッグが成立した操作では必ず遷移しない」を成り立たせる。
    const at = pointerDownAtRef.current;
    if (at && Math.hypot(event.clientX - at.x, event.clientY - at.y) > CLICK_THRESHOLD_PX) {
      return;
    }
    // 配置ツール選択中のダブルクリックは新規配置の操作であり、遷移させない
    // （use-object-drag は配置ツール中の pointerdown をステージへ通す）。
    if (isObjectPlacementTool(activeTool)) return;
    openLinkInNewTab();
  };

  return (
    <div
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="link-card"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onPointerDownCapture={onPointerDownCapture}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: "var(--color-surface)",
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする（box-sizing:border-box のためレイアウトは崩れない）。
        border: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "var(--color-text)",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        // 参照専用はリンクを開けることを示す pointer。編集時は配置ツール・ペン描画中 crosshair・通常 move。
        cursor: readOnly ? "pointer" : usesCrosshairCursor(activeTool) ? "crosshair" : "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: "none",
      }}
      title={
        readOnly
          ? `${object.url}\n（クリックで新規タブを開きます）`
          : `${object.url}\n（ダブルクリック、または Ctrl/Cmd + クリックで新規タブを開きます）`
      }
    >
      {hasImageArea ? (
        imageSrc !== undefined ? (
          // object URL は取得済みのため遅延読み込み（loading="lazy"）の意味が無く、
          // 同一オリジンのため referrerPolicy も不要になる（外部サイトへは要求しない）。
          <img
            src={imageSrc}
            alt=""
            style={{
              width: "100%",
              height: IMAGE_AREA_HEIGHT,
              objectFit: "cover",
              pointerEvents: "none",
            }}
            draggable={false}
            onError={reportRenderError}
          />
        ) : (
          // 取得中と取得失敗のどちらも、壊れた画像アイコンではなく無地のプレースホルダーを出す。
          <div
            data-testid={`link-card-image-placeholder-${object.id}`}
            data-link-card-image-state={imageFailed ? "failed" : "loading"}
            aria-hidden="true"
            style={{
              width: "100%",
              height: IMAGE_AREA_HEIGHT,
              background: "var(--color-surface-muted)",
              pointerEvents: "none",
            }}
          />
        )
      ) : null}
      <div style={{ padding: "12px", flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
        <strong style={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {renderSearchHighlightedText(object.title, search, { dismissible: false })}
        </strong>
        {object.description ? (
          <span style={{ fontSize: "12px", color: "var(--color-text-muted)" }}>
            {object.description}
          </span>
        ) : null}
        <span style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
          {object.siteName ?? safeHostname(object.url)}
        </span>
      </div>
    </div>
  );
}

// window.open に渡してよい URL か（http / https のみ）。
function isHttpUrl(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}
